/**
 * Tipi centralizzati per tutti i form del progetto
 * Include form di preventivo, richieste di lavoro e contatti
 */

// =============================================================================
// FORM DI PREVENTIVO (Quote Request Form)
// =============================================================================

export interface QuoteFormData {
  // Dati personali
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  
  // Dati immobile
  address?: string;
  city?: string;
  propertyType: PropertyType;
  
  // Dettagli servizio
  serviceType: ServiceType;
  problemDescription?: string;
  
  // Privacy e consensi
  privacyAccepted: boolean;
  marketingConsent?: boolean;
  
  // Metadati
  submissionDate: Date;
  source?: string; // Da quale pagina è stato inviato
}

export interface QuoteRequestDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  subtitle?: string;
  defaultService?: string;
  serviceOptions?: { value: string; label: string }[];
}

export interface QuoteRequestFormProps {
  showPopupButton?: boolean;
  popupButtonText?: string;
  popupButtonClassName?: string;
  title?: string;
  subtitle?: string;
  defaultService?: string;
  onSubmit?: (data: QuoteFormData) => void;
  className?: string;
}

// =============================================================================
// FORM DI RICHIESTA LAVORO (Career Form)
// =============================================================================

export interface CareerFormData {
  // Dati personali
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth?: string;
  
  // Residenza
  address?: string;
  city: string;
  province?: string;
  postalCode?: string;
  
  // Esperienza lavorativa
  hasExperience: boolean;
  experienceYears?: number;
  previousRoles?: string;
  skillsDescription?: string;
  
  // Disponibilità
  availability: AvailabilityType;
  startDate?: string;
  workRegions?: string[];
  
  // Documenti
  cvFile?: File;
  coverLetter?: string;
  
  // Privacy e consensi
  privacyAccepted: boolean;
  dataProcessingConsent: boolean;
  
  // Metadati
  submissionDate: Date;
  position?: string; // Posizione per cui si candidata
}

export interface CareerFormProps {
  onSubmit?: (data: CareerFormData) => void;
  className?: string;
  initialValues?: Partial<CareerFormData>;
}

// =============================================================================
// FORM DI CONTATTO GENERALE
// =============================================================================

export interface ContactFormData {
  name: string;
  email: string;
  phone?: string;
  subject?: string;
  message: string;
  privacyAccepted: boolean;
  submissionDate: Date;
}

export interface ContactFormProps {
  onSubmit?: (data: ContactFormData) => void;
  className?: string;
  showSubject?: boolean;
}

// =============================================================================
// TIPI ENUM PER I FORM
// =============================================================================

export type ServiceType = 
  | 'disinfestazione'
  | 'derattizzazione'
  | 'pest-management'
  | 'sanificazione'
  | 'diserbo'
  | 'allontanamento-volatili'
  | 'ispezione-generale'
  | 'consulenza'
  | 'altro';

export type PropertyType = 
  | 'residenziale'
  | 'commerciale'
  | 'industriale'
  | 'uffici'
  | 'ristorante'
  | 'hotel'
  | 'condominio'
  | 'giardino'
  | 'altro';

export type AvailabilityType = 
  | 'immediata'
  | 'entro-1-mese'
  | 'entro-3-mesi'
  | 'flessibile'
  | 'part-time'
  | 'full-time';

// =============================================================================
// OPZIONI PER I SELECT DEI FORM
// =============================================================================

export const SERVICE_TYPE_OPTIONS: Array<{value: ServiceType; label: string}> = [
  { value: 'disinfestazione', label: 'Disinfestazione' },
  { value: 'derattizzazione', label: 'Derattizzazione' },
  { value: 'pest-management', label: 'Pest Management' },
  { value: 'sanificazione', label: 'Sanificazione' },
  { value: 'diserbo', label: 'Diserbo' },
  { value: 'allontanamento-volatili', label: 'Allontanamento Volatili' },
  { value: 'ispezione-generale', label: 'Ispezione Generale' },
  { value: 'consulenza', label: 'Consulenza' },
  { value: 'altro', label: 'Altro' }
];

export const PROPERTY_TYPE_OPTIONS: Array<{value: PropertyType; label: string}> = [
  { value: 'residenziale', label: 'Residenziale' },
  { value: 'commerciale', label: 'Commerciale' },
  { value: 'industriale', label: 'Industriale' },
  { value: 'uffici', label: 'Uffici' },
  { value: 'ristorante', label: 'Ristorante/Bar' },
  { value: 'hotel', label: 'Hotel/B&B' },
  { value: 'condominio', label: 'Condominio' },
  { value: 'giardino', label: 'Giardino/Esterno' },
  { value: 'altro', label: 'Altro' }
];

export const AVAILABILITY_OPTIONS: Array<{value: AvailabilityType; label: string}> = [
  { value: 'immediata', label: 'Disponibilità Immediata' },
  { value: 'entro-1-mese', label: 'Entro 1 mese' },
  { value: 'entro-3-mesi', label: 'Entro 3 mesi' },
  { value: 'flessibile', label: 'Flessibile' },
  { value: 'part-time', label: 'Part-time' },
  { value: 'full-time', label: 'Full-time' }
];

// =============================================================================
// STATI E VALIDAZIONE FORM
// =============================================================================

export interface FormValidationState {
  isValid: boolean;
  errors: Record<string, string>;
  touched: Record<string, boolean>;
}

export interface FormSubmissionState {
  isSubmitting: boolean;
  isSuccess: boolean;
  error?: string;
  submissionId?: string;
}

// =============================================================================
// CONFIGURAZIONE FORM
// =============================================================================

export interface FormConfig {
  showRequiredAsterisk: boolean;
  validateOnBlur: boolean;
  validateOnChange: boolean;
  resetOnSuccess: boolean;
  showSuccessMessage: boolean;
  successMessage?: string;
  errorMessage?: string;
}

// =============================================================================
// HOOKS E UTILITY TYPES
// =============================================================================

export interface UseFormReturn<T> {
  values: T;
  errors: Record<keyof T, string>;
  touched: Record<keyof T, boolean>;
  isSubmitting: boolean;
  isValid: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleChange: (field: keyof T, value: any) => void;
  handleBlur: (field: keyof T) => void;
  handleSubmit: (e: React.FormEvent) => void;
  resetForm: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setFieldValue: (field: keyof T, value: any) => void;
  setFieldError: (field: keyof T, error: string) => void;
}

export type FormFieldProps<T> = {
  name: keyof T;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  error?: string;
  touched?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (value: any) => void;
  onBlur: () => void;
  required?: boolean;
  disabled?: boolean;
};